import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { toast } from "sonner";
import {
  listPlacementRules,
  createPlacementRule,
  togglePlacementRule,
  deletePlacementRule,
  evaluatePlacementRules,
  listPlacementActions,
  listPlacementSummary,
} from "@/lib/placement-rules.functions";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { PageHeader } from "@/components/app/page-header";
import { HelpPanel } from "@/components/app/help-panel";
import { Plus, Play, Trash2, Pause, AlertTriangle } from "lucide-react";

export const Route = createFileRoute("/_authenticated/placement-rules")({
  component: PlacementRulesPage,
});

const CATEGORY_OPTIONS = ["spam", "promotions", "updates", "missing"] as const;

function PlacementRulesPage() {
  const qc = useQueryClient();
  const listRules = useServerFn(listPlacementRules);
  const listActions = useServerFn(listPlacementActions);
  const listSummary = useServerFn(listPlacementSummary);
  const evalFn = useServerFn(evaluatePlacementRules);

  const { data: rulesData } = useQuery({
    queryKey: ["placement-rules"],
    queryFn: () => listRules(),
  });
  const { data: actionsData } = useQuery({
    queryKey: ["placement-actions"],
    queryFn: () => listActions(),
  });
  const { data: summary } = useQuery({
    queryKey: ["placement-summary"],
    queryFn: () => listSummary(),
  });

  const evaluate = useMutation({
    mutationFn: () => evalFn(),
    onSuccess: (r) => {
      toast.success(`Evaluation fertig: ${r.evaluated} Regeln geprüft, ${r.fired} ausgelöst`);
      qc.invalidateQueries({ queryKey: ["placement-rules"] });
      qc.invalidateQueries({ queryKey: ["placement-actions"] });
    },
    onError: (e: Error) => toast.error("Fehler", { description: e.message }),
  });

  const rules = rulesData?.rules ?? [];
  const actions = actionsData?.actions ?? [];
  const counts = summary?.counts ?? {};

  return (
    <div className="space-y-6">
      <PageHeader
        title="Auto-Pause-Regeln"
        description="Regeln, die automatisch eine Kampagne oder Domain pausieren, wenn der Spam- oder Promotions-Anteil im Zeitfenster über einer Schwelle liegt. Quelle der Daten ist die Tabelle der Placement-Ergebnisse (aus Seed-Inbox-Polling)."
        actions={
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => evaluate.mutate()}
              disabled={evaluate.isPending}
            >
              <Play className="size-4 mr-1" />
              Jetzt evaluieren
            </Button>
            <CreateRuleDialog
              onDone={() => qc.invalidateQueries({ queryKey: ["placement-rules"] })}
            />
          </div>
        }
      />

      <HelpPanel
        title="Hilfe: Notbremse — automatisch pausieren bei Spam"
        what={
          <>
            Hier legst du Regeln fest, die <b>automatisch eine Kampagne oder Domain pausieren</b>,
            wenn zu viele Mails im Spam-Ordner landen. Wie eine Sicherung im Stromkasten.
          </>
        }
        why={
          <>
            Bevor du tausend Mails an die Wand fährst, bremst das System selbst. Spart Geld und
            schützt deine Reputation.
          </>
        }
        how={[
          <>
            <b>Neue Regel</b> + Schwelle wählen (z.B. mehr als 30% Spam in 6 Stunden).
          </>,
          <>Ziel wählen: ganze Domain oder einzelne Kampagne.</>,
          <>Speichern. Bei Auslöser pausiert das System sofort und schickt einen Alarm.</>,
          <>
            Wiederaufnehmen erst, wenn das Problem behoben ist (z.B. neuer Betreff, andere Liste).
          </>,
        ]}
        tips={[<>Standardregel: über 25% Spam → pausieren. Das ist ein guter Startwert.</>]}
        glossary={[
          { term: "Placement", def: "Wo Mails landen — Posteingang, Werbung, Spam." },
          { term: "Auto-Pause", def: "Automatischer Stopp des Versands ohne dein Zutun." },
        ]}
      />

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {(["inbox", "spam", "promotions", "updates", "missing"] as const).map((p) => (
          <Card key={p}>
            <CardHeader className="pb-2">
              <CardDescription className="capitalize">{p}</CardDescription>
              <CardTitle className="text-2xl">{counts[p] ?? 0}</CardTitle>
            </CardHeader>
            <CardContent className="text-xs text-muted-foreground">letzte 24h</CardContent>
          </Card>
        ))}
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Regeln ({rules.length})</CardTitle>
          <CardDescription>
            Eine Regel wird ausgelöst, wenn im Fenster mindestens <em>min. Stichproben</em> Mails
            liegen und der Anteil in den gewählten Kategorien die Schwelle erreicht. Nach dem
            Auslösen läuft der Cooldown.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {rules.length === 0 ? (
            <div className="text-center py-10 text-sm text-muted-foreground">
              Noch keine Regel. Klick oben auf <strong>"Regel anlegen"</strong>.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-left text-muted-foreground border-b">
                  <tr>
                    <th className="py-2 pr-4">Aktiv</th>
                    <th className="py-2 pr-4">Name</th>
                    <th className="py-2 pr-4">Bereich</th>
                    <th className="py-2 pr-4">Kategorien</th>
                    <th className="py-2 pr-4">Schwelle</th>
                    <th className="py-2 pr-4">Fenster</th>
                    <th className="py-2 pr-4">Min. Samples</th>
                    <th className="py-2 pr-4">Aktion</th>
                    <th className="py-2 pr-4">Letzter Wert</th>
                    <th className="py-2 pr-4">Zuletzt ausgelöst</th>
                    <th className="py-2 pr-4 text-right"></th>
                  </tr>
                </thead>
                <tbody>
                  {rules.map((r) => (
                    <RuleRow
                      key={r.id}
                      rule={r}
                      onChange={() => qc.invalidateQueries({ queryKey: ["placement-rules"] })}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <AlertTriangle className="size-5 text-amber-500" />
            Auto-Pause-Verlauf
          </CardTitle>
          <CardDescription>Letzte 100 automatische Reaktionen.</CardDescription>
        </CardHeader>
        <CardContent>
          {actions.length === 0 ? (
            <p className="text-sm text-muted-foreground">Bisher keine Auslösungen.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-left text-muted-foreground border-b">
                  <tr>
                    <th className="py-2 pr-4">Zeit</th>
                    <th className="py-2 pr-4">Regel</th>
                    <th className="py-2 pr-4">Aktion</th>
                    <th className="py-2 pr-4">Ziel</th>
                    <th className="py-2 pr-4">% / Stichprobe</th>
                    <th className="py-2 pr-4">Notiz</th>
                  </tr>
                </thead>
                <tbody>
                  {actions.map((a) => (
                    <tr key={a.id} className="border-b last:border-0">
                      <td className="py-2 pr-4 text-xs">
                        {new Date(a.performed_at).toLocaleString()}
                      </td>
                      <td className="py-2 pr-4">{a.rule_name}</td>
                      <td className="py-2 pr-4">
                        <Badge variant={a.action === "alert" ? "secondary" : "destructive"}>
                          {a.action}
                        </Badge>
                      </td>
                      <td className="py-2 pr-4 font-mono text-xs">{a.target_label}</td>
                      <td className="py-2 pr-4">
                        {a.observed_percent}% · {a.sample_count}
                      </td>
                      <td className="py-2 pr-4 text-xs text-muted-foreground">{a.note}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

type Rule = {
  id: string;
  name: string;
  enabled: boolean;
  scope_type: string;
  scope_id: string | null;
  scope_domain: string | null;
  placement_categories: string[];
  window_minutes: number;
  min_samples: number;
  threshold_percent: number;
  action: string;
  last_value: number;
  last_sample_count: number;
  last_fired_at: string | null;
};

function RuleRow({ rule, onChange }: { rule: Rule; onChange: () => void }) {
  const toggleFn = useServerFn(togglePlacementRule);
  const delFn = useServerFn(deletePlacementRule);

  const toggle = useMutation({
    mutationFn: (enabled: boolean) => toggleFn({ data: { id: rule.id, enabled } }),
    onSuccess: () => onChange(),
    onError: (e: Error) => toast.error("Fehler", { description: e.message }),
  });
  const del = useMutation({
    mutationFn: () => delFn({ data: { id: rule.id } }),
    onSuccess: () => {
      toast.success("Regel gelöscht");
      onChange();
    },
    onError: (e: Error) => toast.error("Fehler", { description: e.message }),
  });

  const scopeLabel =
    rule.scope_type === "global"
      ? "alle"
      : rule.scope_type === "campaign"
        ? `Kampagne ${rule.scope_id?.slice(0, 8) ?? "?"}…`
        : (rule.scope_domain ?? "—");

  return (
    <tr className="border-b last:border-0">
      <td className="py-2 pr-4">
        <Switch checked={rule.enabled} onCheckedChange={(v) => toggle.mutate(v)} />
      </td>
      <td className="py-2 pr-4 font-medium">{rule.name}</td>
      <td className="py-2 pr-4 text-xs">
        {rule.scope_type} · {scopeLabel}
      </td>
      <td className="py-2 pr-4 text-xs">{rule.placement_categories.join(", ")}</td>
      <td className="py-2 pr-4">≥ {rule.threshold_percent}%</td>
      <td className="py-2 pr-4">{rule.window_minutes} min</td>
      <td className="py-2 pr-4">{rule.min_samples}</td>
      <td className="py-2 pr-4">
        {rule.action === "alert" && <Badge variant="secondary">nur warnen</Badge>}
        {rule.action === "pause_campaign" && (
          <Badge className="bg-amber-500/15 text-amber-700 border-amber-500/30">
            <Pause className="size-3 mr-1" />
            Kampagne
          </Badge>
        )}
        {rule.action === "pause_domain" && (
          <Badge variant="destructive">
            <Pause className="size-3 mr-1" />
            Domain
          </Badge>
        )}
      </td>
      <td className="py-2 pr-4">
        {rule.last_value}%{" "}
        <span className="text-xs text-muted-foreground">/ {rule.last_sample_count}</span>
      </td>
      <td className="py-2 pr-4 text-xs">
        {rule.last_fired_at ? new Date(rule.last_fired_at).toLocaleString() : "nie"}
      </td>
      <td className="py-2 pr-4 text-right">
        <Button size="sm" variant="ghost" className="text-destructive" onClick={() => del.mutate()}>
          <Trash2 className="size-4" />
        </Button>
      </td>
    </tr>
  );
}

function CreateRuleDialog({ onDone }: { onDone: () => void }) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [scopeType, setScopeType] = useState<"global" | "campaign" | "domain">("global");
  const [scopeId, setScopeId] = useState("");
  const [scopeDomain, setScopeDomain] = useState("");
  const [categories, setCategories] = useState<string[]>(["spam", "promotions"]);
  const [windowMin, setWindowMin] = useState(60);
  const [minSamples, setMinSamples] = useState(5);
  const [thresholdPct, setThresholdPct] = useState(30);
  const [action, setAction] = useState<"alert" | "pause_campaign" | "pause_domain">("alert");
  const [cooldown, setCooldown] = useState(60);
  const createFn = useServerFn(createPlacementRule);

  const allowedActions =
    scopeType === "campaign"
      ? (["alert", "pause_campaign"] as const)
      : scopeType === "domain"
        ? (["alert", "pause_domain"] as const)
        : (["alert"] as const);

  const create = useMutation({
    mutationFn: () =>
      createFn({
        data: {
          name,
          enabled: true,
          scope_type: scopeType,
          scope_id: scopeType === "campaign" ? scopeId : null,
          scope_domain: scopeType === "domain" ? scopeDomain : null,
          placement_categories: categories as ("spam" | "promotions" | "updates" | "missing")[],
          window_minutes: windowMin,
          min_samples: minSamples,
          threshold_percent: thresholdPct,
          action: allowedActions.includes(action as never) ? action : "alert",
          cooldown_minutes: cooldown,
        },
      }),
    onSuccess: () => {
      toast.success("Regel angelegt");
      setOpen(false);
      setName("");
      setScopeId("");
      setScopeDomain("");
      onDone();
    },
    onError: (e: Error) => toast.error("Fehler", { description: e.message }),
  });

  function toggleCat(cat: string, checked: boolean) {
    setCategories((prev) =>
      checked ? [...new Set([...prev, cat])] : prev.filter((c) => c !== cat),
    );
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button>
          <Plus className="size-4 mr-1" />
          Regel anlegen
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Auto-Pause-Regel</DialogTitle>
          <DialogDescription>
            Pausiert automatisch eine Kampagne oder Domain, wenn zu viele Testmails in
            Spam/Promotions landen.
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-4 py-2">
          <div className="grid gap-2">
            <Label>Name</Label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Spam > 30% in 60min → Domain pausieren"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="grid gap-2">
              <Label>Geltungsbereich</Label>
              <Select
                value={scopeType}
                onValueChange={(v: "global" | "campaign" | "domain") => setScopeType(v)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="global">Global (alle Kampagnen)</SelectItem>
                  <SelectItem value="campaign">Eine Kampagne</SelectItem>
                  <SelectItem value="domain">Eine Domain</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid gap-2">
              <Label>Aktion bei Verletzung</Label>
              <Select
                value={action}
                onValueChange={(v: "alert" | "pause_campaign" | "pause_domain") => setAction(v)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="alert">Nur warnen</SelectItem>
                  {scopeType === "campaign" && (
                    <SelectItem value="pause_campaign">Kampagne pausieren</SelectItem>
                  )}
                  {scopeType === "domain" && (
                    <SelectItem value="pause_domain">Domain pausieren</SelectItem>
                  )}
                </SelectContent>
              </Select>
            </div>
          </div>

          {scopeType === "campaign" && (
            <div className="grid gap-2">
              <Label>Kampagnen-ID (UUID)</Label>
              <Input
                value={scopeId}
                onChange={(e) => setScopeId(e.target.value)}
                placeholder="uuid…"
              />
            </div>
          )}
          {scopeType === "domain" && (
            <div className="grid gap-2">
              <Label>Domain</Label>
              <Input
                value={scopeDomain}
                onChange={(e) => setScopeDomain(e.target.value)}
                placeholder="meineasykredit.click"
              />
            </div>
          )}

          <div className="grid gap-2">
            <Label>Negativ-Kategorien (mind. eine)</Label>
            <div className="flex gap-4 flex-wrap">
              {CATEGORY_OPTIONS.map((c) => (
                <label key={c} className="flex items-center gap-2 text-sm">
                  <Checkbox
                    checked={categories.includes(c)}
                    onCheckedChange={(v) => toggleCat(c, !!v)}
                  />
                  {c}
                </label>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-4 gap-3">
            <div className="grid gap-2">
              <Label>Fenster (min)</Label>
              <Input
                type="number"
                value={windowMin}
                onChange={(e) => setWindowMin(Number(e.target.value) || 60)}
              />
            </div>
            <div className="grid gap-2">
              <Label>Min. Samples</Label>
              <Input
                type="number"
                value={minSamples}
                onChange={(e) => setMinSamples(Number(e.target.value) || 5)}
              />
            </div>
            <div className="grid gap-2">
              <Label>Schwelle %</Label>
              <Input
                type="number"
                value={thresholdPct}
                onChange={(e) => setThresholdPct(Number(e.target.value) || 30)}
              />
            </div>
            <div className="grid gap-2">
              <Label>Cooldown (min)</Label>
              <Input
                type="number"
                value={cooldown}
                onChange={(e) => setCooldown(Number(e.target.value) || 60)}
              />
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => setOpen(false)}>
            Abbrechen
          </Button>
          <Button
            onClick={() => create.mutate()}
            disabled={create.isPending || !name || categories.length === 0}
          >
            {create.isPending ? "Speichere…" : "Regel anlegen"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
